import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Editorial hero carousel: images advance on interval; videos play fully once, then advance.
 */
class PfEditorialHeroCarousel extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;

  /** @type {IntersectionObserver | null} */
  #slideObserver = null;

  /** @type {HTMLVideoElement | null} */
  #activeVideo = null;

  /** @type {((this: HTMLVideoElement, ev: Event) => void) | null} */
  #onVideoEnded = null;

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #paused = false;

  connectedCallback() {
    super.connectedCallback();

    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    this.#bindDots();
    this.#observeSlides();
    this.#bindInteractionPause();
    this.#activateSlide(0);

    if (slides.length <= 1) return;

    if (this.dataset.autoplay === 'true' && !prefersReducedMotion()) {
      this.#scheduleAdvance();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearAdvance();
    this.#unbindActiveVideo();
    this.#slideObserver?.disconnect();
    this.#pauseAllVideos();
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('.pf-editorial-hero__track');
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return [...this.querySelectorAll('.pf-editorial-hero__slide')];
  }

  /** @returns {HTMLButtonElement[]} */
  get #dots() {
    return [...this.querySelectorAll('[data-hero-dot]')];
  }

  /** @returns {boolean} */
  get #autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !prefersReducedMotion();
  }

  #bindDots() {
    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.#goTo(index);
      });
    });
  }

  #observeSlides() {
    const track = this.#track;
    if (!track) return;

    this.#slideObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.55)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visible?.target) return;

        const index = this.#slides.indexOf(/** @type {HTMLElement} */ (visible.target));
        if (index < 0 || index === this.#index) return;

        this.#index = index;
        this.#syncDots();
        this.#activateSlide(index);
        if (this.#autoplayEnabled && !this.#paused) this.#scheduleAdvance();
      },
      { root: track, threshold: [0.55, 0.75] }
    );

    this.#slides.forEach((slide) => this.#slideObserver?.observe(slide));
  }

  #bindInteractionPause() {
    const pause = () => {
      this.#paused = true;
      this.#clearAdvance();
      this.#activeVideo?.pause();
    };
    const resume = () => {
      this.#paused = false;
      if (!this.#autoplayEnabled) return;

      const video = this.#activeVideo;
      if (video && !video.ended) {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
        // Advance still bound to `ended` from #activateSlide.
        return;
      }

      this.#scheduleAdvance();
    };

    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node} */ (event.relatedTarget))) resume();
    });

    const track = this.#track;
    if (!track) return;

    let resumeTimeout = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
    track.addEventListener(
      'touchstart',
      () => {
        pause();
        if (resumeTimeout) clearTimeout(resumeTimeout);
      },
      { passive: true }
    );
    track.addEventListener(
      'touchend',
      () => {
        if (resumeTimeout) clearTimeout(resumeTimeout);
        resumeTimeout = setTimeout(resume, 2500);
      },
      { passive: true }
    );
  }

  #clearAdvance() {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #scheduleAdvance() {
    this.#clearAdvance();
    if (this.#paused || !this.#autoplayEnabled || this.#slides.length <= 1) return;

    const slide = this.#slides[this.#index];
    const video = /** @type {HTMLVideoElement | null} */ (slide?.querySelector('video') || null);

    // Videos advance when they finish (handled in #activateSlide).
    if (video) return;

    const interval = Math.max(2500, Number(this.dataset.interval || 5000));
    this.#timer = setTimeout(() => {
      this.#goTo((this.#index + 1) % this.#slides.length);
    }, interval);
  }

  /**
   * @param {number} index
   */
  #goTo(index) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;

    this.#index = index;
    track.scrollTo({
      left: slide.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
    this.#syncDots();
    this.#activateSlide(index);

    if (this.#autoplayEnabled && !this.#paused) {
      this.#scheduleAdvance();
    } else {
      this.#clearAdvance();
    }
  }

  #syncDots() {
    this.#dots.forEach((dot, index) => {
      const selected = index === this.#index;
      dot.setAttribute('aria-selected', selected ? 'true' : 'false');
      dot.classList.toggle('is-active', selected);
      if (selected) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #unbindActiveVideo() {
    if (this.#activeVideo && this.#onVideoEnded) {
      this.#activeVideo.removeEventListener('ended', this.#onVideoEnded);
    }
    this.#activeVideo = null;
    this.#onVideoEnded = null;
  }

  /**
   * @param {number} activeIndex
   */
  #activateSlide(activeIndex) {
    this.#unbindActiveVideo();

    this.#slides.forEach((slide, index) => {
      const video = /** @type {HTMLVideoElement | null} */ (slide.querySelector('video'));
      if (!video) return;

      if (index === activeIndex && !prefersReducedMotion()) {
        this.#activeVideo = video;
        video.muted = true;
        video.loop = false;

        this.#onVideoEnded = () => {
          if (this.#paused || !this.#autoplayEnabled || this.#slides.length <= 1) return;
          this.#goTo((this.#index + 1) % this.#slides.length);
        };
        video.addEventListener('ended', this.#onVideoEnded);

        try {
          video.currentTime = 0;
        } catch {
          // Ignore seek errors on unloaded media.
        }

        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      } else {
        video.pause();
        try {
          video.currentTime = 0;
        } catch {
          // Ignore seek errors on unloaded media.
        }
      }
    });
  }

  #pauseAllVideos() {
    this.querySelectorAll('video').forEach((node) => {
      const video = /** @type {HTMLVideoElement} */ (node);
      video.pause();
    });
  }
}

if (!customElements.get('pf-editorial-hero-carousel')) {
  customElements.define('pf-editorial-hero-carousel', PfEditorialHeroCarousel);
}
